/**
 * Condition expressions gate scene connections and chapter exits.
 *
 * Grammar (case-insensitive keywords, whitespace ignored):
 *
 *   or      := and ( OR and )*
 *   and     := unary ( AND unary )*
 *   unary   := NOT unary | primary
 *   primary := "(" or ")" | predicate
 *   predicate := ("has" | "flag" | "visited") ":" key
 *   key     := [A-Za-z0-9_.-]+
 *
 * Precedence: NOT > AND > OR. An empty expression is always true.
 */
export class ConditionError extends Error {
    position;
    constructor(message, position) {
        super(`${message} (at position ${position})`);
        this.position = position;
        this.name = "ConditionError";
    }
}
const KEY_RE = /^[A-Za-z0-9_.-]+/;
const WORD_RE = /^[A-Za-z_][A-Za-z0-9_]*/;
function tokenize(input) {
    const tokens = [];
    let i = 0;
    while (i < input.length) {
        const ch = input[i];
        if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
            i++;
            continue;
        }
        if (ch === "(") {
            tokens.push({ kind: "lparen", pos: i });
            i++;
            continue;
        }
        if (ch === ")") {
            tokens.push({ kind: "rparen", pos: i });
            i++;
            continue;
        }
        const rest = input.slice(i);
        const word = WORD_RE.exec(rest);
        if (!word) {
            throw new ConditionError(`Unexpected character '${ch}'`, i);
        }
        const w = word[0];
        const lower = w.toLowerCase();
        const afterWord = i + w.length;
        if (input[afterWord] === ":") {
            if (lower !== "has" && lower !== "flag" && lower !== "visited") {
                throw new ConditionError(`Unknown predicate '${w}'. Expected has:, flag:, or visited:`, i);
            }
            const keyMatch = KEY_RE.exec(input.slice(afterWord + 1));
            if (!keyMatch) {
                throw new ConditionError(`Missing key after '${w}:'`, afterWord + 1);
            }
            tokens.push({ kind: "pred", pred: lower, key: keyMatch[0], pos: i });
            i = afterWord + 1 + keyMatch[0].length;
            continue;
        }
        if (lower === "not") {
            tokens.push({ kind: "not", pos: i });
        }
        else if (lower === "and") {
            tokens.push({ kind: "and", pos: i });
        }
        else if (lower === "or") {
            tokens.push({ kind: "or", pos: i });
        }
        else {
            throw new ConditionError(`Unexpected word '${w}'. Expected NOT, AND, OR, or a predicate like has:key`, i);
        }
        i = afterWord;
    }
    tokens.push({ kind: "eof", pos: input.length });
    return tokens;
}
class Parser {
    tokens;
    idx = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    peek() {
        return this.tokens[this.idx];
    }
    next() {
        return this.tokens[this.idx++];
    }
    parse() {
        const result = this.parseOr();
        const t = this.peek();
        if (t.kind !== "eof") {
            throw new ConditionError(t.kind === "rparen" ? "Unmatched ')'" : `Unexpected token`, t.pos);
        }
        return result;
    }
    parseOr() {
        const operands = [this.parseAnd()];
        while (this.peek().kind === "or") {
            this.next();
            operands.push(this.parseAnd());
        }
        return operands.length === 1 ? operands[0] : { type: "or", operands };
    }
    parseAnd() {
        const operands = [this.parseUnary()];
        while (this.peek().kind === "and") {
            this.next();
            operands.push(this.parseUnary());
        }
        return operands.length === 1 ? operands[0] : { type: "and", operands };
    }
    parseUnary() {
        if (this.peek().kind === "not") {
            this.next();
            return { type: "not", operand: this.parseUnary() };
        }
        return this.parsePrimary();
    }
    parsePrimary() {
        const t = this.next();
        switch (t.kind) {
            case "lparen": {
                const inner = this.parseOr();
                const close = this.next();
                if (close.kind !== "rparen") {
                    throw new ConditionError("Expected ')'", close.pos);
                }
                return inner;
            }
            case "pred":
                return { type: t.pred, key: t.key };
            case "eof":
                throw new ConditionError("Unexpected end of expression", t.pos);
            case "rparen":
                throw new ConditionError("Unexpected ')'", t.pos);
            default:
                throw new ConditionError(`Expected a predicate or '('`, t.pos);
        }
    }
}
/** Parse a condition expression. Throws ConditionError on bad syntax. */
export function parseCondition(expr) {
    if (expr == null || expr.trim() === "") {
        return { type: "true" };
    }
    return new Parser(tokenize(expr)).parse();
}
/** Evaluate a parsed condition against play state. */
export function evaluateCondition(cond, state) {
    switch (cond.type) {
        case "true":
            return true;
        case "has":
            return state.inventory.includes(cond.key);
        case "flag":
            return state.flags[cond.key] === true;
        case "visited":
            return state.visited.includes(cond.key);
        case "not":
            return !evaluateCondition(cond.operand, state);
        case "and":
            return cond.operands.every((c) => evaluateCondition(c, state));
        case "or":
            return cond.operands.some((c) => evaluateCondition(c, state));
    }
}
/** Convenience: parse and evaluate in one step. */
export function checkCondition(expr, state) {
    return evaluateCondition(parseCondition(expr), state);
}
/** Returns null if valid, otherwise the error message. */
export function validateCondition(expr) {
    try {
        parseCondition(expr);
        return null;
    }
    catch (e) {
        return e instanceof Error ? e.message : String(e);
    }
}
//# sourceMappingURL=condition.js.map